import React, { useEffect, useState, useCallback, useRef } from 'react';
import { formatDateDMY } from '../utils/dateFormat';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { searchAPI } from '../utils/api';

const fmtDate = d => formatDateDMY(d, '');
const fmtNum = n => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { darkMode, color } = useTheme();
  const { userType } = useAuth();
  const accent = color?.swatch || '#3B82F6';
  const [search, setSearch] = useState('');
  const [liveResults, setLiveResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (open) { setSearch(''); setLiveResults(null); }
  }, [open]);

  // Debounced live search against the backend
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!search.trim() || search.trim().length < 2 || userType === 'admin') {
      setLiveResults(null);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchAPI.global(search.trim());
        setLiveResults(res.data);
      } catch {
        setLiveResults(null);
      }
      setSearching(false);
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search, userType]);

  const runAction = useCallback((action) => {
    onClose();
    if (action.navigate) {
      navigate(action.navigate);
    }
    if (action.fn) {
      // Small delay to let the palette close first
      setTimeout(() => action.fn(), 100);
    }
  }, [navigate, onClose]);

  // Dispatch custom event so pages can listen for modal opens
  const openModal = useCallback((modalName, route) => {
    onClose();
    const currentPath = window.location.pathname;
    if (route && currentPath !== route) {
      navigate(route + '?action=create&source=' + encodeURIComponent(currentPath));
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('sv-open-modal', { detail: modalName }));
      }, 300);
    } else {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('sv-open-modal', { detail: modalName }));
      }, 100);
    }
  }, [onClose, navigate]);

  const clientPages = [
    { label: 'Dashboard',         icon: '📊', shortcut: 'Alt+D', navigate: '/dashboard' },
    { label: 'Parties',           icon: '👥', shortcut: 'Alt+A', navigate: '/parties' },
    { label: 'Products',          icon: '📦', shortcut: 'Alt+P', navigate: '/products' },
    { label: 'Stock Audit',       icon: '📋', shortcut: 'Alt+U', navigate: '/stock-audit' },
    { label: 'Billing',           icon: '💰', shortcut: 'Alt+B', navigate: '/billing' },
    { label: 'Quotations',        icon: '📋', shortcut: 'Alt+Q', navigate: '/quotations' },
    { label: 'Payments',          icon: '💳', shortcut: 'Alt+Y', navigate: '/payments' },
    { label: 'Store Management',  icon: '🏬', shortcut: 'Alt+M', navigate: '/store-management' },
    { label: 'Marketing',         icon: '📣', shortcut: 'Alt+K', navigate: '/marketing' },
    { label: 'Reports',           icon: '📈', shortcut: 'Alt+R', navigate: '/reports' },
    { label: 'Settings',          icon: '⚙️', shortcut: 'Alt+S', navigate: '/settings' },
  ];

  const adminPages = [
    { label: 'Dashboard',    icon: '📊', shortcut: 'Alt+D', navigate: '/dashboard' },
    { label: 'Shop Owners',  icon: '🏪', shortcut: 'Alt+O', navigate: '/admin' },
    { label: 'Settings',     icon: '⚙️', shortcut: 'Alt+S', navigate: '/settings' },
  ];

  const pages = userType === 'admin' ? adminPages : clientPages;

  const quickActions = [
    { label: 'Create Invoice',       icon: '🧾', shortcut: 'F1', fn: () => {
      onClose();
      if (window.location.pathname !== '/billing') {
        navigate('/billing?action=create&source=' + encodeURIComponent(window.location.pathname));
      } else {
        setTimeout(() => window.dispatchEvent(new CustomEvent('sv-open-modal', { detail: 'new-invoice' })), 100);
      }
    }},
    { label: 'Start Stock Audit',    icon: '📋', fn: () => {
      onClose();
      navigate('/stock-audit');
    }},
    { label: 'Create Purchase Bill', icon: '🏭', shortcut: 'F2', fn: () => {
      onClose();
      if (window.location.pathname !== '/billing') {
        navigate('/billing?action=create&mode=purchase&source=' + encodeURIComponent(window.location.pathname));
      } else {
        setTimeout(() => window.dispatchEvent(new CustomEvent('sv-open-modal', { detail: 'purchase-bill' })), 100);
      }
    }},
    { label: 'Add Product',          icon: '📦', shortcut: 'F3', fn: () => openModal('add-product', '/products') },
    { label: 'Add Customer',         icon: '👤', shortcut: 'F4', fn: () => openModal('add-customer', '/parties') },
    { label: 'Add Supplier',         icon: '🏭', shortcut: 'F5', fn: () => openModal('add-supplier', '/parties') },
    { label: 'New Quotation',        icon: '📋', shortcut: 'F6', fn: () => openModal('new-quotation', '/quotations') },
    { label: 'Record Payment',       icon: '💳', shortcut: 'F7', fn: () => openModal('new-payment', '/payments') },
    { label: 'Send Message',         icon: '✉️', shortcut: 'F8', fn: () => {
      onClose();
      if (window.location.pathname !== '/marketing') {
        navigate('/marketing?action=compose&source=' + encodeURIComponent(window.location.pathname));
      } else {
        setTimeout(() => window.dispatchEvent(new CustomEvent('sv-open-modal', { detail: 'send-message' })), 100);
      }
    }},
  ];

  const toolActions = [
    { label: 'Focus Search',        icon: '🔍', shortcut: 'Ctrl+K',  fn: () => { const s = document.querySelector('input[placeholder*="Search"], input[placeholder*="search"]'); if (s) s.focus(); } },
    { label: 'Toggle Dark Mode',    icon: '🌙', fn: () => { const { toggleDark } = require('../contexts/ThemeContext'); /* handled via event */ window.dispatchEvent(new CustomEvent('sv-toggle-dark')); } },
    { label: 'Keyboard Shortcuts',  icon: '⌨️', shortcut: '?',       fn: () => window.dispatchEvent(new CustomEvent('sv-show-shortcuts')) },
  ];

  if (!open) return null;

  const bg = darkMode ? '#1E293B' : '#FFFFFF';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const mutedText = darkMode ? '#64748B' : '#94A3B8';
  const text = darkMode ? '#F1F5F9' : '#0F172A';

  return (
    <div 
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: 'min(20vh, 120px)',
      }}
      className="modal-overlay"
    >
      <div onClick={e => e.stopPropagation()} className="modal-content" style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 16,
        overflow: 'hidden',
        boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
        width: 'min(600px, calc(100vw - 32px))',
      }}>
        <Command label="Command palette" shouldFilter>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '14px 16px',
            borderBottom: `1px solid ${border}`,
          }}>
            <span style={{ fontSize: 18, opacity: searching ? 1 : 0.5 }}>{searching ? '⏳' : '🔍'}</span>
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder="Search invoices, customers, suppliers, products… or type a command"
              autoFocus
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: 15,
                color: text,
                fontFamily: 'inherit',
              }}
            />
            <kbd style={{
              fontSize: 10, fontFamily: 'monospace', fontWeight: 700,
              padding: '3px 6px', borderRadius: 5,
              background: darkMode ? '#0F172A' : '#F1F5F9',
              color: mutedText,
              border: `1px solid ${border}`,
            }}>ESC</kbd>
          </div>
          <Command.List style={{
            maxHeight: 'min(520px, 65vh)',
            overflowY: 'auto',
            padding: 8,
          }}>
            <Command.Empty style={{
              padding: 32, textAlign: 'center',
              color: mutedText, fontSize: 13,
            }}>
              {searching ? 'Searching…' : 'No results found.'}
            </Command.Empty>

            {/* ── Live data search results (shown when user typed ≥2 chars) ── */}
            {liveResults && (() => {
              const { invoices = [], purchase_bills = [], customers = [], suppliers = [], products = [] } = liveResults;
              const hasAny = invoices.length || purchase_bills.length || customers.length || suppliers.length || products.length;
              return (
                <>
                  {searching && (
                    <div style={{ padding: '8px 16px', color: mutedText, fontSize: 12 }}>Searching…</div>
                  )}
                  {!hasAny && !searching && (
                    <div style={{ padding: '12px 16px', color: mutedText, fontSize: 13, textAlign: 'center' }}>No matching records found for "{search}"</div>
                  )}

                  {invoices.length > 0 && (
                    <Command.Group heading="🧾 Invoices">
                      {invoices.map(inv => {
                        const statusColor = inv.payment_status === 'paid' ? '#16a34a' : inv.payment_status === 'unpaid' ? '#dc2626' : '#d97706';
                        return (
                          <Command.Item key={inv.id} value={`inv-${inv.id}-${inv.invoice_number}-${inv.customer_name}`}
                            onSelect={() => { onClose(); navigate(`/billing?highlight=${inv.id}`); }}
                            style={{ padding: '9px 12px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: text }}>
                            <span style={{ fontSize: 14, width: 24, textAlign: 'center' }}>🧾</span>
                            <span style={{ flex: 1 }}>
                              <span style={{ fontWeight: 700, color: accent, fontFamily: 'monospace' }}>{inv.invoice_number}</span>
                              <span style={{ marginLeft: 8 }}>{inv.customer_name}</span>
                            </span>
                            <span style={{ fontSize: 11, color: statusColor, fontWeight: 700, whiteSpace: 'nowrap' }}>
                              ₹{fmtNum(inv.grand_total)} · {inv.payment_status}
                            </span>
                            <span style={{ fontSize: 11, color: mutedText, whiteSpace: 'nowrap' }}>{fmtDate(inv.created_at)}</span>
                          </Command.Item>
                        );
                      })}
                    </Command.Group>
                  )}

                  {purchase_bills.length > 0 && (
                    <Command.Group heading="🏭 Purchase Bills">
                      {purchase_bills.map(pb => {
                        const statusColor = pb.payment_status === 'paid' ? '#16a34a' : pb.payment_status === 'unpaid' ? '#dc2626' : '#d97706';
                        return (
                          <Command.Item key={pb.id} value={`pb-${pb.id}-${pb.bill_number}-${pb.supplier_name}`}
                            onSelect={() => { onClose(); navigate(`/billing?tab=purchase&highlight=${pb.id}`); }}
                            style={{ padding: '9px 12px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: text }}>
                            <span style={{ fontSize: 14, width: 24, textAlign: 'center' }}>🏭</span>
                            <span style={{ flex: 1 }}>
                              <span style={{ fontWeight: 700, color: accent, fontFamily: 'monospace' }}>{pb.bill_number || '—'}</span>
                              <span style={{ marginLeft: 8 }}>{pb.supplier_name}</span>
                            </span>
                            <span style={{ fontSize: 11, color: statusColor, fontWeight: 700, whiteSpace: 'nowrap' }}>
                              ₹{fmtNum(pb.grand_total)} · {pb.payment_status}
                            </span>
                            <span style={{ fontSize: 11, color: mutedText, whiteSpace: 'nowrap' }}>{fmtDate(pb.bill_date)}</span>
                          </Command.Item>
                        );
                      })}
                    </Command.Group>
                  )}

                  {customers.length > 0 && (
                    <Command.Group heading="👥 Customers">
                      {customers.map(c => (
                        <Command.Item key={c.id} value={`cust-${c.id}-${c.name}-${c.mobile || ''}-${c.email || ''}`}
                          onSelect={() => { onClose(); navigate('/parties?mode=customer'); }}
                          style={{ padding: '9px 12px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: text }}>
                          <span style={{ fontSize: 14, width: 24, textAlign: 'center' }}>👤</span>
                          <span style={{ flex: 1 }}>
                            <span style={{ fontWeight: 700 }}>{c.name}</span>
                            {c.mobile && <span style={{ marginLeft: 8, color: mutedText, fontSize: 12 }}>{c.mobile}</span>}
                          </span>
                          {c.city && <span style={{ fontSize: 11, color: mutedText }}>{c.city}</span>}
                          {c.gstin && <span style={{ fontSize: 11, color: mutedText, fontFamily: 'monospace' }}>{c.gstin}</span>}
                        </Command.Item>
                      ))}
                    </Command.Group>
                  )}

                  {suppliers.length > 0 && (
                    <Command.Group heading="🏭 Suppliers">
                      {suppliers.map(s => (
                        <Command.Item key={s.id} value={`sup-${s.id}-${s.name}-${s.phone || ''}`}
                          onSelect={() => { onClose(); navigate('/parties?mode=supplier'); }}
                          style={{ padding: '9px 12px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: text }}>
                          <span style={{ fontSize: 14, width: 24, textAlign: 'center' }}>🏭</span>
                          <span style={{ flex: 1 }}>
                            <span style={{ fontWeight: 700 }}>{s.name}</span>
                            {s.phone && <span style={{ marginLeft: 8, color: mutedText, fontSize: 12 }}>{s.phone}</span>}
                          </span>
                          {s.city && <span style={{ fontSize: 11, color: mutedText }}>{s.city}</span>}
                          {s.gstin && <span style={{ fontSize: 11, color: mutedText, fontFamily: 'monospace' }}>{s.gstin}</span>}
                        </Command.Item>
                      ))}
                    </Command.Group>
                  )}

                  {products.length > 0 && (
                    <Command.Group heading="📦 Products">
                      {products.map(p => (
                        <Command.Item key={p.id} value={`prod-${p.id}-${p.name}-${p.sku || ''}`}
                          onSelect={() => { onClose(); navigate('/products'); }}
                          style={{ padding: '9px 12px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: text }}>
                          <span style={{ fontSize: 14, width: 24, textAlign: 'center' }}>📦</span>
                          <span style={{ flex: 1 }}>
                            <span style={{ fontWeight: 700 }}>{p.name}</span>
                            {p.sku && <span style={{ marginLeft: 8, color: mutedText, fontSize: 12 }}>#{p.sku}</span>}
                          </span>
                          <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>₹{fmtNum(p.selling_price)}</span>
                          {p.category && <span style={{ fontSize: 11, color: mutedText }}>{p.category}</span>}
                        </Command.Item>
                      ))}
                    </Command.Group>
                  )}
                </>
              );
            })()}

            {/* ── Navigation & Actions (always shown when no live results yet or still typing) ── */}
            {!liveResults && (
              <>
                <Command.Group heading="Pages">
                  {pages.map(item => (
                    <Command.Item
                      key={item.label}
                      value={item.label}
                      onSelect={() => runAction(item)}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        cursor: 'pointer',
                        fontSize: 14,
                        color: text,
                      }}
                    >
                      <span style={{ fontSize: 16, width: 24, textAlign: 'center' }}>{item.icon}</span>
                      <span style={{ flex: 1 }}>{item.label}</span>
                      {item.shortcut && (
                        <kbd style={{
                          fontSize: 10, fontFamily: 'monospace', fontWeight: 600,
                          padding: '2px 6px', borderRadius: 4,
                          background: darkMode ? '#0F172A' : '#F1F5F9',
                          color: accent,
                          border: `1px solid ${border}`,
                        }}>{item.shortcut}</kbd>
                      )}
                    </Command.Item>
                  ))}
                </Command.Group>

                {userType !== 'admin' && (
                  <Command.Group heading="Quick Actions">
                    {quickActions.map(item => (
                      <Command.Item
                        key={item.label}
                        value={item.label}
                        onSelect={() => runAction(item)}
                        style={{
                          padding: '10px 12px',
                          borderRadius: 8,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          cursor: 'pointer',
                          fontSize: 14,
                          color: text,
                        }}
                      >
                        <span style={{ fontSize: 16, width: 24, textAlign: 'center' }}>{item.icon}</span>
                        <span style={{ flex: 1 }}>{item.label}</span>
                        {item.shortcut && (
                          <kbd style={{
                            fontSize: 10, fontFamily: 'monospace', fontWeight: 600,
                            padding: '2px 6px', borderRadius: 4,
                            background: darkMode ? '#0F172A' : '#F1F5F9',
                            color: accent,
                            border: `1px solid ${border}`,
                          }}>{item.shortcut}</kbd>
                        )}
                      </Command.Item>
                    ))}
                  </Command.Group>
                )}

                <Command.Group heading="Tools">
                  {toolActions.map(item => (
                    <Command.Item
                      key={item.label}
                      value={item.label}
                      onSelect={() => runAction(item)}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        cursor: 'pointer',
                        fontSize: 14,
                        color: text,
                      }}
                    >
                      <span style={{ fontSize: 16, width: 24, textAlign: 'center' }}>{item.icon}</span>
                      <span style={{ flex: 1 }}>{item.label}</span>
                      {item.shortcut && (
                        <kbd style={{
                          fontSize: 10, fontFamily: 'monospace', fontWeight: 600,
                          padding: '2px 6px', borderRadius: 4,
                          background: darkMode ? '#0F172A' : '#F1F5F9',
                          color: accent,
                          border: `1px solid ${border}`,
                        }}>{item.shortcut}</kbd>
                      )}
                    </Command.Item>
                  ))}
                </Command.Group>
              </>
            )}
          </Command.List>

          <div style={{
            borderTop: `1px solid ${border}`,
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: 11,
            color: mutedText,
          }}>
            <span>↑↓ Navigate · ↵ Select · Esc Close {search.length >= 2 && !liveResults ? '· Searching data…' : search.length >= 2 && liveResults ? '· Showing live results' : '· Type 2+ chars to search data'}</span>
            <span style={{ fontWeight: 600, color: accent }}>Smart Vyapari</span>
          </div>
        </Command>
      </div>
    </div>
  );
};

export default CommandPalette;
